/**
 * ========================================
 * BLESSING SERVICE - CHECKOUT MODAL VERSION
 * ========================================
 * Shows a modal popup when user clicks checkout button
 * Popup only appears if:
 * - Cart has physical products
 * - Blessing service is NOT in cart
 * 
 * Features:
 * - Modal popup with warm, spiritual design
 * - Dynamic pricing (free/threshold mode)
 * - Checkout interception
 * - Max 1 blessing service enforcement
 * - No embedded widgets in cart
 * ========================================
 */
(function () {
  'use strict';

  // =========================
  // CONFIG
  // =========================
  const CONFIG = {
    virtualProductTitle: 'Bless Your Craft on the Stone of Anointing',
    virtualProductHandle: 'bless-your-craft-on-the-stone-of-anointing-at-holy-sepulchre-church-of-jerusalem',

    // Default pricing settings (used if Liquid snippet not found)
    defaultPricingMode: 'threshold', // 'free' or 'threshold'
    defaultThresholdUSD: 200.0,
    defaultPriceUSD: 20.0,
    
    // Default modal content (can be overridden by metafields)
    defaultModalImage: 'https://cdn.shopify.com/s/files/1/0910/0086/5135/files/IMG_3767_3c007536-da2a-47a2-aea2-4c4a0c5de83b.png?v=1761767563',
    defaultModalHeading: 'Would you like to add a Blessing Service?',
    defaultModalDescription: 'We will place your item on the <strong>Stone of Anointing</strong> in <strong>Holy Sepulchre Church</strong>, where <strong>Jesus\' body was anointed for burial</strong>.',
    defaultButtonYesText: 'Bless My Order',
    defaultButtonNoText: 'Proceed Without Blessing',
  };

  let isProcessing = false;
  let modalContent = null;
  let modalShown = false;

  // =========================
  // METAFIELD LOADER (FROM LIQUID)
  // =========================
  function loadModalContent() {
    if (modalContent) return modalContent;

    try {
      const configElement = document.getElementById('blessing-service-config');
      
      if (configElement) {
        const config = JSON.parse(configElement.textContent);
        console.log('📄 Parsed config:', config);
        modalContent = {
          image: config.modal_image || CONFIG.defaultModalImage,
          heading: config.modal_heading || CONFIG.defaultModalHeading,
          description: config.modal_description || CONFIG.defaultModalDescription,
          buttonYesText: config.button_yes_text || CONFIG.defaultButtonYesText,
          buttonNoText: config.button_no_text || CONFIG.defaultButtonNoText
        };
      } else {
        modalContent = {
          image: CONFIG.defaultModalImage,
          heading: CONFIG.defaultModalHeading,
          description: CONFIG.defaultModalDescription,
          buttonYesText: CONFIG.defaultButtonYesText,
          buttonNoText: CONFIG.defaultButtonNoText
        };
      }
      
      return modalContent;
    } catch (error) {
      modalContent = {
        image: CONFIG.defaultModalImage,
        heading: CONFIG.defaultModalHeading,
        description: CONFIG.defaultModalDescription,
        buttonYesText: CONFIG.defaultButtonYesText,
        buttonNoText: CONFIG.defaultButtonNoText
      };
      return modalContent;
    }
  }
  
  function loadPricingSettings() {
    try {
      const configElement = document.getElementById('blessing-service-config');
      console.log('🔍 Config element found:', !!configElement);
      
      if (configElement) {
        const config = JSON.parse(configElement.textContent);
        console.log('📄 Parsed config:', config);
        const settings = {
          mode: config.pricing_mode || CONFIG.defaultPricingMode,
          thresholdUSD: parseFloat(config.threshold_amount) || CONFIG.defaultThresholdUSD,
          priceUSD: config.price !== undefined && config.price !== null ? parseFloat(config.price) : CONFIG.defaultPriceUSD
        };
        console.log('✅ Loaded pricing settings:', settings);
        return settings;
      } else {
        console.log('⚠️ Config element not found, using defaults');
        return {
          mode: CONFIG.defaultPricingMode,
          thresholdUSD: CONFIG.defaultThresholdUSD,
          priceUSD: CONFIG.defaultPriceUSD
        };
      }
    } catch (error) {
      console.error('❌ Error loading pricing settings:', error);
      return {
        mode: CONFIG.defaultPricingMode,
        thresholdUSD: CONFIG.defaultThresholdUSD,
        priceUSD: CONFIG.defaultPriceUSD
      };
    }
  }

  // =========================
  // HELPERS
  // =========================
  async function getCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      return await r.json();
    } catch (e) {
      console.error('Error fetching cart:', e);
      return null;
    }
  }

  async function addToCart(variantId, quantity = 1) {
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
        credentials: 'same-origin',
      });
      return await r.json();
    } catch (e) {
      console.error('Error adding to cart:', e);
      return null;
    }
  }

  async function updateCartItem(lineKey, quantity) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity }),
        credentials: 'same-origin',
      });
      return await r.json();
    } catch (e) {
      console.error('Error updating cart:', e);
      return null;
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function isVirtualProduct(item) {
    if (item.handle) {
      return item.handle === CONFIG.virtualProductHandle;
    }
    if (item.product_title) {
      return item.product_title === 'Bless Your Craft on the Stone of Anointing at Holy Sepulchre Church of Jerusalem';
    }
    return false;
  }

  function isPrayerService(item) {
    return item.product_type && item.product_type === 'Prayer';
  }
  
  function isServiceBlessing(item) {
    return item.product_type && item.product_type === 'Service-Blessing';
  }

  function isPhysicalProduct(item) {
    return !isVirtualProduct(item) && !isPrayerService(item) && !isServiceBlessing(item);
  }

  // =========================
  // CART ANALYSIS
  // =========================
  function analyzeCart(cart) {
    if (!cart || !cart.items) return null;

    const virtualItems = cart.items.filter(isVirtualProduct);
    const physicalItems = cart.items.filter(isPhysicalProduct);
    const hasPhysicalProducts = physicalItems.length > 0;

    // Calculate cart total excluding blessing service
    const cartTotalExcludingBlessing = cart.items
      .filter(item => !isVirtualProduct(item))
      .reduce((sum, item) => sum + item.final_line_price, 0);

    const cartTotalUSD = cartTotalExcludingBlessing / 100;

    return {
      hasVirtual: virtualItems.length > 0,
      virtualItems: virtualItems,
      virtualCount: virtualItems.length,
      hasPhysicalProducts,
      physicalCount: physicalItems.length,
      cartTotalUSD,
      cartTotalExcludingBlessing
    };
  }

  // =========================
  // PRICING LOGIC
  // =========================
  function calculateBlessingPrice(cartTotalUSD) {
    const settings = loadPricingSettings();
    console.log('🔍 calculateBlessingPrice:', { cartTotalUSD, settings });

    if (settings.mode === 'free') {
      console.log('✅ Mode is FREE');
      return {
        isFree: true,
        price: 0,
        message: '✨ Blessing Service is FREE with your order!',
        status: 'free'
      };
    } else {
      if (cartTotalUSD >= settings.thresholdUSD) {
        console.log('✅ Cart total >= threshold, FREE!');
        return {
          isFree: true,
          price: 0,
          message: '🎉 Blessing Service is now FREE!',
          status: 'free_threshold'
        };
      } else {
        const remaining = settings.thresholdUSD - cartTotalUSD;
        console.log('💰 Cart total < threshold, PAID. Remaining:', remaining);
        return {
          isFree: false,
          price: settings.priceUSD,
          message: `Add ${formatMoney(remaining * 100)} more to get FREE Blessing Service!`,
          status: 'paid',
          remaining: remaining
        };
      }
    }
  }

  // =========================
  // ENFORCE MAX 1 BLESSING SERVICE
  // =========================
  async function enforceMaxOneBlessingService() {
    const cart = await getCart();
    if (!cart) return;

    const analysis = analyzeCart(cart);
    if (!analysis) return;

    // If more than 1 blessing service, reduce to 1
    if (analysis.virtualCount > 1) {
      // Keep the first one, remove the rest
      for (let i = 1; i < analysis.virtualItems.length; i++) {
        await updateCartItem(analysis.virtualItems[i].key, 0);
      }
      
      // Also ensure the first one has quantity 1
      if (analysis.virtualItems[0].quantity > 1) {
        await updateCartItem(analysis.virtualItems[0].key, 1);
      }
    } else if (analysis.virtualCount === 1 && analysis.virtualItems[0].quantity > 1) {
      // If only 1 blessing service but quantity > 1, set to 1
      await updateCartItem(analysis.virtualItems[0].key, 1);
    }
  }

  // =========================
  // MODAL POPUP
  // =========================
  function createModal(pricing) {
    // Remove existing modal if any
    const existingModal = document.getElementById('blessing-service-modal');
    if (existingModal) {
      existingModal.remove();
    }

    const content = loadModalContent();

    const modal = document.createElement('div');
    modal.id = 'blessing-service-modal';
    modal.innerHTML = `
      <div class="blessing-modal-backdrop"></div>
      <div class="blessing-modal-content">
        <button class="blessing-modal-close" aria-label="Close">×</button>
        <div class="blessing-modal-image">
          <img src="${content.image}" alt="Blessing Service" />
        </div>
        <div class="blessing-modal-text">
          <h2 class="blessing-modal-heading">${content.heading}</h2>
          <p class="blessing-modal-description">${content.description}</p>
          <div class="blessing-modal-pricing">
            ${pricing.isFree 
              ? '<span class="blessing-price-free">✨ FREE with your order!</span>' 
              : `<span class="blessing-price-paid">Price: ${formatMoney(pricing.price * 100)}</span><br><span class="blessing-price-hint">${pricing.message}</span>`
            }
          </div>
          <div class="blessing-modal-buttons">
            <button class="blessing-btn blessing-btn-yes">${content.buttonYesText}</button>
            <button class="blessing-btn blessing-btn-no">${content.buttonNoText}</button>
          </div>
        </div>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      #blessing-service-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .blessing-modal-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
      }
      
      .blessing-modal-content {
        position: relative;
        background: white;
        border-radius: 16px;
        max-width: 900px;
        width: 90%;
        max-height: 90vh;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        animation: modalSlideIn 0.3s ease-out;
        display: flex;
        flex-direction: row;
      }
      
      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: translateY(-50px) scale(0.9);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      
      .blessing-modal-close {
        position: absolute;
        top: 16px;
        right: 16px;
        background: transparent;
        border: none;
        font-size: 32px;
        line-height: 1;
        cursor: pointer;
        color: #333;
        z-index: 10;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
      }
      
      .blessing-modal-close:hover {
        color: #000;
        transform: scale(1.1);
      }
      
      .blessing-modal-image {
        flex: 0 0 45%;
        background: #f5f5f5;
        overflow: hidden;
      }
      
      .blessing-modal-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      
      .blessing-modal-text {
        flex: 1;
        padding: 48px 40px 40px 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      
      .blessing-modal-heading {
        margin: 0 0 20px 0;
        font-size: 26px;
        font-weight: 700;
        color: #1a1a1a;
        line-height: 1.3;
      }
      
      .blessing-modal-description {
        font-size: 16px;
        line-height: 1.6;
        color: #333;
        margin: 0 0 20px 0;
      }
      
      .blessing-modal-description strong {
        font-weight: 700;
        color: #1a1a1a;
      }
      
      .blessing-modal-pricing {
        background: #f9f9f9;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        padding: 16px;
        text-align: center;
        margin: 0 0 24px 0;
      }
      
      .blessing-price-free {
        font-size: 20px;
        font-weight: 700;
        color: #2e7d32;
      }
      
      .blessing-price-paid {
        font-size: 18px;
        font-weight: 600;
        color: #5d4e37;
      }
      
      .blessing-price-hint {
        font-size: 14px;
        color: #8b7355;
        font-style: italic;
      }
      
      .blessing-modal-buttons {
        display: flex;
        gap: 12px;
        flex-direction: column;
      }
      
      .blessing-btn {
        padding: 16px 24px;
        font-size: 16px;
        font-weight: 600;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        font-family: inherit;
        text-align: center;
      }
      
      .blessing-btn-yes {
        background: #2c2c2c;
        color: white;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }
      
      .blessing-btn-yes:hover {
        background: #1a1a1a;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
        transform: translateY(-2px);
      }
      
      .blessing-btn-no {
        background: transparent;
        color: #666;
        border: 2px solid #e0e0e0;
        box-shadow: none;
      }
      
      .blessing-btn-no:hover {
        background: #f5f5f5;
        border-color: #ccc;
        color: #333;
        transform: translateY(-2px);
      }
      
      .blessing-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none !important;
      }
      
      @media (max-width: 768px) {
        .blessing-modal-content {
          flex-direction: column;
          max-width: 95%;
          max-height: 95vh;
        }
        
        .blessing-modal-image {
          flex: 0 0 250px;
          max-height: 250px;
        }
        
        .blessing-modal-text {
          padding: 32px 24px 24px 24px;
        }
        
        .blessing-modal-heading {
          font-size: 22px;
        }
        
        .blessing-modal-description {
          font-size: 14px;
        }
        
        .blessing-btn {
          padding: 14px 20px;
          font-size: 15px;
        }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(modal);

    return modal;
  }

  function showModal(pricing, onYes, onNo) {
    if (modalShown) return; // Prevent multiple modals
    modalShown = true;

    const modal = createModal(pricing);
    const yesBtn = modal.querySelector('.blessing-btn-yes');
    const noBtn = modal.querySelector('.blessing-btn-no');
    const closeBtn = modal.querySelector('.blessing-modal-close');
    const backdrop = modal.querySelector('.blessing-modal-backdrop');

    const closeModal = () => {
      modal.remove();
      modalShown = false;
    };

    yesBtn.addEventListener('click', async () => {
      yesBtn.disabled = true;
      noBtn.disabled = true;
      yesBtn.textContent = '⏳ Adding...';
      
      await onYes();
      closeModal();
    });

    noBtn.addEventListener('click', () => {
      onNo();
      closeModal();
    });

    closeBtn.addEventListener('click', () => {
      onNo();
      closeModal();
    });

    backdrop.addEventListener('click', () => {
      onNo();
      closeModal();
    });
  }

  // =========================
  // CHECKOUT INTERCEPTION
  // =========================
  async function interceptCheckout(event, checkoutUrl) {
    // Prevent default checkout
    event.preventDefault();
    event.stopPropagation();

    if (isProcessing) return;
    isProcessing = true;

    try {
      // Enforce max 1 blessing service
      await enforceMaxOneBlessingService();

      // Get fresh cart data
      const cart = await getCart();
      if (!cart) {
        // If can't get cart, just proceed to checkout
        window.location.href = checkoutUrl;
        return;
      }

      const analysis = analyzeCart(cart);
      if (!analysis) {
        window.location.href = checkoutUrl;
        return;
      }

      // Check if we should show the modal
      const shouldShowModal = analysis.hasPhysicalProducts && !analysis.hasVirtual;

      if (shouldShowModal) {
        const pricing = calculateBlessingPrice(analysis.cartTotalUSD);

        showModal(
          pricing,
          // On Yes - Add blessing service
          async () => {
            try {
              // Fetch product to get variant ID
              const response = await fetch(`/products/${CONFIG.virtualProductHandle}.js`);
              if (response.ok) {
                const product = await response.json();
                const variantId = product.variants[0].id;
                await addToCart(variantId, 1);
                
                // Wait a moment for cart to update before redirecting
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            } catch (error) {
              console.error('Error adding blessing service:', error);
            }
            
            // Proceed to checkout page
            isProcessing = false;
            window.location.href = '/checkout';
          },
          // On No - Just proceed to checkout
          () => {
            isProcessing = false;
            window.location.href = '/checkout';
          }
        );
      } else {
        // No need to show modal, proceed to checkout
        isProcessing = false;
        window.location.href = '/checkout';
      }
    } catch (error) {
      console.error('Error in checkout interception:', error);
      isProcessing = false;
      window.location.href = '/checkout';
    }
  }

  // =========================
  // PAYPAL BUTTON PROTECTION
  // =========================
  function protectPayPalButton() {
    // Ensure PayPal and other express checkout buttons are always visible
    const expressCheckoutSelectors = [
      '.shopify-payment-button',
      '[data-shopify="payment-button"]',
      '.dynamic-checkout__content',
      '.additional-checkout-buttons',
      '.shopify-payment-button__button',
      '.paypal-button',
      '#paypal-button',
      '[aria-label*="PayPal"]',
      '[aria-label*="Apple Pay"]',
      '[aria-label*="Google Pay"]',
      '[aria-label*="Shop Pay"]'
    ];

    expressCheckoutSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (el) {
          el.style.display = '';
          el.style.visibility = 'visible';
          el.style.opacity = '1';
          el.style.pointerEvents = 'auto';
          el.style.position = '';
          el.style.zIndex = '';
          
          // Remove any hiding classes
          el.classList.remove('hidden');
          el.classList.remove('hide');
          el.classList.remove('d-none');
        }
      });
    });
  }

  // =========================
  // INITIALIZATION
  // =========================
  function init() {
    // Note: loadPricingSettings() is called when needed, not on init
    
    // Protect PayPal button immediately
    protectPayPalButton();

    // Function to attach listeners to checkout buttons
    function attachCheckoutListeners() {
      // Common checkout button selectors
      const selectors = [
        '[name="checkout"]',
        'button[name="checkout"]',
        'input[name="checkout"]',
        '.cart__checkout',
        '#checkout',
        '[href="/checkout"]',
        '[href*="/checkout"]',
        'button[type="submit"][form*="cart"]',
        '.cart-drawer__footer button[type="submit"]',
        '.cart__ctas button',
        '.cart-drawer__checkout'
      ];

      const checkoutButtons = document.querySelectorAll(selectors.join(', '));

      checkoutButtons.forEach(button => {
        // Skip if already has listener
        if (button.dataset.blessingListenerAttached) return;
        
        // IMPORTANT: Skip express checkout buttons (PayPal, Apple Pay, Google Pay, Shop Pay, etc.)
        const isExpressCheckout = 
          button.closest('.shopify-payment-button') ||
          button.closest('[data-shopify="payment-button"]') ||
          button.closest('.dynamic-checkout__content') ||
          button.closest('.additional-checkout-buttons') ||
          button.classList.contains('shopify-payment-button__button') ||
          button.classList.contains('paypal-button') ||
          button.id === 'paypal-button' ||
          button.getAttribute('aria-label')?.includes('PayPal') ||
          button.getAttribute('aria-label')?.includes('Apple Pay') ||
          button.getAttribute('aria-label')?.includes('Google Pay') ||
          button.getAttribute('aria-label')?.includes('Shop Pay');
        
        if (isExpressCheckout) {
          return; // Don't intercept express checkout buttons
        }
        
        button.dataset.blessingListenerAttached = 'true';

        button.addEventListener('click', (e) => {
          // Determine checkout URL
          let checkoutUrl = '/checkout';
          
          if (button.tagName === 'A') {
            checkoutUrl = button.href;
          } else if (button.form) {
            checkoutUrl = button.form.action || '/checkout';
          }

          interceptCheckout(e, checkoutUrl);
        }, true); // Use capture phase to intercept before other handlers
      });
    }

    // Initial attachment
    setTimeout(attachCheckoutListeners, 500);

    // Re-attach when cart drawer opens or cart updates
    document.addEventListener('cart:updated', () => {
      setTimeout(attachCheckoutListeners, 300);
    });

    document.addEventListener('cart-drawer:open', () => {
      setTimeout(attachCheckoutListeners, 300);
    });

    // Observe DOM changes to catch dynamically added checkout buttons
    const observer = new MutationObserver(() => {
      attachCheckoutListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Enforce max 1 blessing service on cart updates
    document.addEventListener('cart:updated', () => {
      setTimeout(enforceMaxOneBlessingService, 500);
    });

    // Initial enforcement
    setTimeout(enforceMaxOneBlessingService, 1000);
    
    // =========================
    // PAYPAL BUTTON PROTECTION
    // =========================
    // Run protection every 200ms
    setInterval(protectPayPalButton, 200);

    // Also run after cart updates
    document.addEventListener('cart:refresh', protectPayPalButton);
    document.addEventListener('cart:updated', protectPayPalButton);
    
    // Watch for DOM changes - Monitor for PayPal button visibility
    const paypalObserver = new MutationObserver(protectPayPalButton);
    paypalObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
